import { AxiosError, AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ErrorInfo, UserSearch } from '../../../api/model';
import { usePostUsersDelete } from '../../../api/users/users';
import { useDefaultOptions } from '../../../hooks/api';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import ErrorComponent from '../../atoms/ErrorComponent';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';
import ConfirmModal from '../../modules/ConfirmModal';
import PageNavi from '../../modules/PageNavi';
import { UserDownload } from './UserDownload';
import { UserRemove } from './UserRemove';
import { UserTakeover } from './takeover/UserTakeOver';

interface Props {
  data: AxiosResponse<UserSearch, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  error: AxiosError<ErrorInfo> | null;
  refetch: () => void;
}

const N_DATA_PER_PAGE = 50;

// 一覧のソートキー
type SortKey =
  | 'user_id_text'
  | 'user_name_ja'
  | 'user_name_en'
  | 'company_name'
  | 'group_name'
  | 'chapter_name'
  | 'privilege_name'
  | 'updated_at';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

function UserList({ data, isLoading, isError, error, refetch }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);
  const [selectUserIds, setSelectUsers] = useState<number[]>([]);
  const [isConfirm, setIsConfirm] = useState(false);
  const [Editable, setEditable] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Users.CurrentPage', 1));

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Users.SortInfo', { key: 'user_id_text' as SortKey, isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 編集フラグ
  useEffect(() => {
    if (data == null || data.data == null || data.data.editable == null) {
      setEditable(false);
    } else {
      setEditable(Boolean(data.data.editable));
    }
  }, [data, setEditable]);

  // 一覧データをソートしたもの
  const sortedUsers = useMemo(
    () =>
      [...(data?.data.user_lsit_items ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, data]
  );
  // 現在のページに表示する一覧
  const usersForPresentPage = useMemo(
    () => sortedUsers.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedUsers]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Users.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Users.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.user_lsit_items != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [data?.data.user_lsit_items]);

  const handleAllSelectUser = useCallback(() => {
    if (data?.data.user_lsit_items !== undefined) {
      if (selectUserIds.length === data?.data.user_lsit_items.length) {
        setSelectUsers([]);
      } else {
        const userIds: number[] = [];
        data?.data.user_lsit_items.forEach((user) => {
          if (user.user_id) userIds.push(user.user_id);
        });
        setSelectUsers(userIds);
      }
    }
  }, [setSelectUsers, selectUserIds, data?.data.user_lsit_items]);

  const handleSelectUser = useCallback(
    (userId: number) => {
      if (selectUserIds.includes(userId)) {
        setSelectUsers(selectUserIds.filter((id) => id !== userId));
      } else {
        setSelectUsers([...selectUserIds, userId]);
      }
    },
    [setSelectUsers, selectUserIds]
  );

  // 削除ボタン
  const { mutateAsync, isSuccess: isDeleteSuccess } = usePostUsersDelete<AxiosError<ErrorInfo>, unknown>(options);
  const handleDeleteUsers = () => {
    const postUserDelete = async () => {
      await mutateAsync({
        data: { user_id: selectUserIds },
      });
    };
    void postUserDelete();
    setIsConfirm(false);
    setIsDeleted(true);
  };
  useEffect(() => {
    if (isDeleted && isDeleteSuccess) {
      refetch();
      setIsDeleted(false);
    }
  }, [isDeleted, isDeleteSuccess, refetch]);

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={error} />;
  }

  // データが空の場合
  if (data == null || data.data.user_lsit_items == null) {
    return <NoDataComponent />;
  }

  return (
    <div>
      <div className="ttlsep prolist">
        <div className="left">
          <h3 className="trig">
            <span>{t('ユーザー一覧')}</span>
          </h3>
        </div>
        <div className="right">
          <ul>
            <li>
              <Link
                to={`/${language}/users/edit/0`}
                state={{ transitionSource: 'users' }}
                style={{
                  color: Editable ? '' : '#9d9d9d',
                  pointerEvents: Editable ? 'auto' : 'none',
                }}
              >
                <i className="fa-solid fa-circle-plus" />
                {t('ユーザー追加')}
              </Link>
            </li>
            <li>
              <UserTakeover Editable={Editable} />
            </li>
            <li>
              <UserDownload />
            </li>
          </ul>
        </div>
      </div>
      <div className="wfr in-pgnavi">
        <div className="outputfr mb15">
          <a
            href="#user"
            onClick={() => setIsConfirm(true)}
            style={{
              color: Editable ? '' : '#9d9d9d',
              pointerEvents: Editable ? 'auto' : 'none',
            }}
          >
            <i
              className="fa-solid fa-circle-xmark"
              style={{
                color: Editable ? '' : '#9d9d9d',
                pointerEvents: Editable ? 'auto' : 'none',
              }}
            />
            {t('ユーザー削除')}
          </a>
        </div>
        <table className="table40 mb30">
          <tbody>
            <tr>
              <th>
                {' '}
                <span className="ck-box">
                  <input
                    type="checkbox"
                    id="ck59-1"
                    checked={usersForPresentPage.length === selectUserIds.length}
                    onChange={() => handleAllSelectUser()}
                  />{' '}
                  <label htmlFor="ck59-1">&nbsp;</label>
                </span>
              </th>
              <SortableTh label={t('ユーザーID')} onClick={() => changeSortKey('user_id_text')} />
              <SortableTh label={t('ユーザー名（日）')} onClick={() => changeSortKey('user_name_ja')} />
              <SortableTh label={t('ユーザー名（英）')} onClick={() => changeSortKey('user_name_en')} />
              <SortableTh label={t('会社名')} onClick={() => changeSortKey('company_name')} />
              <SortableTh label={t('所属')} onClick={() => changeSortKey('group_name')} />
              <SortableTh label={t('担当章')} onClick={() => changeSortKey('chapter_name')} />
              <SortableTh label={t('権限名')} onClick={() => changeSortKey('privilege_name')} />
              <SortableTh label={t('更新日')} onClick={() => changeSortKey('updated_at')} />
              <th> </th>
            </tr>
            {usersForPresentPage.map((x) => (
              <tr key={x.user_id}>
                <td>
                  {!x.deleted ? (
                  <span className="ck-box">
                  <input
                    type="checkbox"
                    id={`user_${String(x.user_id)}`}
                    checked={x.user_id ? selectUserIds.includes(x.user_id) : false}
                    onChange={() => x.user_id && handleSelectUser(x.user_id)}
                  />
                  <label htmlFor={`user_${String(x.user_id)}`}>&nbsp;</label>
                </span>
                  ) : (
                    <span> </span>
                  )}
                </td>
                <td>
                  <Link
                    to={`/${language}/users/edit/${x.user_id?.toString() ?? ''}`}
                    state={{ transitionSource: 'users' }}
                  >
                    {x.user_id_text}
                  </Link>
                </td>
                <td>{x.user_name_ja}</td>
                <td>{x.user_name_en}</td>
                <td>{x.company_name}</td>
                <td>{x.group_name}</td>
                <td>{x.chapter_name}</td>
                <td>{x.privilege_name}</td>
                <td style={{ whiteSpace: 'pre' }}>{x.updated_at && format(new Date(x.updated_at), 'yy-MM-dd')}</td>
                <UserRemove user={x} refetch={refetch} />
              </tr>
            ))}
          </tbody>
        </table>
        <PageNavi
          current={currentPage}
          pageCount={Math.ceil(data.data.user_lsit_items.length / (N_DATA_PER_PAGE * 1.0))}
          onChangePage={changeCurrentPage}
        />
      </div>
      {isConfirm && (
        <ConfirmModal onClickOk={() => handleDeleteUsers()} onClickCancel={() => setIsConfirm(false)}>
          <div className="poptxt">
            <p>{t('削除します。よろしいですか？')}</p>
          </div>
        </ConfirmModal>
      )}
    </div>
  );
}
export { UserList };
